import React, { useState, useRef, useCallback, useMemo } from 'react'
import { inject, observer } from 'mobx-react'
import { apiUrl } from '../../../../config'
import {
  PictureOutlined,
  LoadingOutlined,
  GlobalOutlined,
  UsergroupAddOutlined,
  SmileOutlined,
} from '@ant-design/icons'
import { message, Progress, Select, Tag } from 'antd'
import SelectPeoplePopup from '../../../../components/SelectPeoplePopup/SelectPeoplePopup'
import { FileRequest } from '../../../../requests/FileRequest'
import {
  ComposerContainer,
  ComposerHeader,
  UserAvatar,
  ComposerInput,
  ComposerActionsRow,
  ComposerActionButtons,
  ComposerActionButton,
  ComposerSubmitButton,
  colors,
} from '../../NewsFeedStyled'
import ComposerMediaEditor from '../ComposerMediaEditor/ComposerMediaEditor'
import NewsFeedEditor from '../NewsFeedEditor/NewsFeedEditor'
import FeelingSelectModal from '../FeelingSelectModal/FeelingSelectModal'
import { FEELINGS } from '../../constants/newsFeedConstants'
import utils from '../../../../utils'

const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024 // 100MB
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime']
const MAX_IMAGES = 20
const MAX_TEXT_LENGTH = 5000

const PostComposer = ({
  currentUser,
  onPostCreated,
  newsFeedStore,
  selectPeopleStore,
  authenticationStore,
  userStore,
}) => {
  const [text, setText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [mediaFiles, setMediaFiles] = useState([])
  const [isFocused, setIsFocused] = useState(false)
  const [visibility, setVisibility] = useState('PUBLIC')
  const [showSelectPeople, setShowSelectPeople] = useState(false)
  const [customViewers, setCustomViewers] = useState([])
  const [selectedFeeling, setSelectedFeeling] = useState(null)
  const [showFeelingModal, setShowFeelingModal] = useState(false)
  const [showSelectPeopleTagged, setShowSelectPeopleTagged] = useState(false)
  const [taggedUsers, setTaggedUsers] = useState([])

  const fileInputRef = useRef(null)

  const hasContent =
    text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim().length > 0 ||
    mediaFiles.length > 0 ||
    selectedFeeling !== null

  // Chọn file chung (ảnh/video)
  const handleFileSelect = useCallback(
    e => {
      const files = Array.from(e.target.files)
      const remainSlots = MAX_IMAGES - mediaFiles.length
      const selectedFiles = files.slice(0, remainSlots)

      const validFiles = []
      for (const file of selectedFiles) {
        if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
          if (file.size > MAX_IMAGE_SIZE) {
            message.warning(`${file.name} vượt quá 10MB`)
            continue
          }
          validFiles.push({
            file,
            preview: URL.createObjectURL(file),
            type: 'IMAGE',
            id: Date.now() + Math.random(),
            caption: '',
            fileName: file.name,
          })
        } else if (ALLOWED_VIDEO_TYPES.includes(file.type)) {
          if (file.size > MAX_VIDEO_SIZE) {
            message.warning(`${file.name} vượt quá 100MB`)
            continue
          }
          validFiles.push({
            file,
            preview: URL.createObjectURL(file),
            type: 'VIDEO',
            id: Date.now() + Math.random(),
            caption: '',
            fileName: file.name,
          })
        } else {
          validFiles.push({
            file,
            preview: null,
            type: file.type === 'application/pdf' ? 'PDF' : 'DOCUMENT',
            id: Date.now() + Math.random(),
            caption: '',
            fileName: file.name,
          })
        }
      }

      if (validFiles.length > 0) {
        setMediaFiles(prev => [...prev, ...validFiles])
      }
      e.target.value = ''
    },
    [mediaFiles]
  )

  // Xóa 1 file
  const removeMedia = useCallback(id => {
    setMediaFiles(prev => {
      const media = prev.find(i => i.id === id)
      if (media && media.preview) URL.revokeObjectURL(media.preview)
      return prev.filter(i => i.id !== id)
    })
  }, [])

  // Xóa tất cả file đã chọn
  const clearAllMedia = useCallback(() => {
    setMediaFiles(prev => {
      prev.forEach(m => {
        if (m.preview) URL.revokeObjectURL(m.preview)
      })
      return []
    })
  }, [])

  // Duplicate insertMention removed; earlier implementation retained

  // Submit bài viết
  const handleSubmit = useCallback(async () => {
    if (!hasContent || isSubmitting) return

    try {
      setIsSubmitting(true)
      setUploadProgress(0)

      // Set upload progress callback
      window.__uploadProgressCallback = percent => {
        setUploadProgress(percent)
      }

      const formData = new FormData()
      const rawTextTrimmed = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim()
      if (rawTextTrimmed.length > 0 || text.trim().length > 0) {
        let finalContent = text.trim()
        if (selectedFeeling) {
          finalContent = `<!--feeling:${JSON.stringify(
            selectedFeeling
          )}-->\n${finalContent}`
        }
        formData.append('content', finalContent)
      } else if (selectedFeeling) {
        formData.append(
          'content',
          `<!--feeling:${JSON.stringify(selectedFeeling)}-->\n`
        )
      }
      formData.append('visibility', visibility)
      if (selectedFeeling) {
        formData.append('feeling', JSON.stringify(selectedFeeling))
      }
      // Trích xuất người dùng được tag qua @mention từ nội dung CKEditor
      const mentionMatches = [...text.matchAll(/data-user-id="([^"]+)"/g)]
      const mentionedUserIds = mentionMatches.map(m => m[1])
      if (mentionedUserIds.length > 0) {
        const allUsers = [
          ...(userStore?.mentionUserList || []),
          ...(userStore?.userList || []),
        ]
        const extractedUsers = mentionedUserIds.map(id => {
          const u = allUsers.find(
            item => String(item.id || item.username) === String(id)
          )
          return {
            id,
            display: u?.display || u?.fullName || u?.full_name || id,
          }
        })
        formData.append('mentionedUsers', JSON.stringify(extractedUsers))
      }
      if (visibility === 'CUSTOM' && customViewers.length > 0) {
        customViewers.forEach(v => {
          formData.append('customViewers', v)
        })
      }
      if (taggedUsers.length > 0) {
        taggedUsers.forEach(v => {
          formData.append('taggedUsers', v)
        })
      }

      // Upload API - TẤT CẢ các file (Ảnh, Video, PDF, Document) đều upload qua API /upload để lấy fileId
      const fileIds = []
      const mediaCaptions = []
      let i = 0
      for (const m of mediaFiles) {
        const fileFormData = new FormData()
        fileFormData.append('file', m.file)
        const uploadRes = await FileRequest.uploadFile(fileFormData)
        if (
          uploadRes &&
          uploadRes.data &&
          (uploadRes.data.file_id || uploadRes.data.fileId)
        ) {
          const fId = uploadRes.data.file_id || uploadRes.data.fileId
          fileIds.push(fId)
          mediaCaptions.push((m.caption || '').trim())
        } else {
          throw new Error(`Upload ${m.type} thất bại`)
        }
        i++
        setUploadProgress(Math.floor((i / mediaFiles.length) * 50))
      }

      if (fileIds.length > 0) {
        fileIds.forEach(id => formData.append('fileIds', id))
        mediaCaptions.forEach(caption => formData.append('mediaCaptions', caption))
      }

      await newsFeedStore.createPost(formData)

      // Reset form
      setText('')
      setSelectedFeeling(null)
      setVisibility('PUBLIC')
      mediaFiles.forEach(m => {
        if (m.preview) URL.revokeObjectURL(m.preview)
      })
      setMediaFiles([])
      setCustomViewers([])
      setTaggedUsers([])
      if (selectPeopleStore) {
        selectPeopleStore.selectUserData['CUSTOM_VIEWERS'] = []
        selectPeopleStore.selectUserData['TAGGED_USERS'] = []
      }
      setIsFocused(false)

      if (onPostCreated) onPostCreated()
    } catch (err) {
      console.error('Post error:', err)
      message.error(err?.vi || 'Đăng bài thất bại!')
    } finally {
      setIsSubmitting(false)
      setUploadProgress(0)
      window.__uploadProgressCallback = null
    }
  }, [
    text,
    selectedFeeling,
    mediaFiles,
    visibility,
    customViewers,
    taggedUsers,
    hasContent,
    isSubmitting,
    onPostCreated,
    selectPeopleStore,
    newsFeedStore,
    userStore,
  ])


  const handleRemoveViewer = useCallback(
    userId => {
      setCustomViewers(prev => prev.filter(id => id !== userId))
      if (
        selectPeopleStore &&
        selectPeopleStore.selectUserData['CUSTOM_VIEWERS']
      ) {
        selectPeopleStore.selectUserData[
          'CUSTOM_VIEWERS'
        ] = selectPeopleStore.selectUserData['CUSTOM_VIEWERS'].filter(
          u => u.id !== userId
        )
      }
    },
    [selectPeopleStore]
  )

  const userName = currentUser?.name_uppercase || 'Người dùng'
  const { currentUserAvatar } = authenticationStore
  const avatarSrc = currentUserAvatar
    ? URL.createObjectURL(currentUserAvatar)
    : null

  const handleSelectCustomViewers = () => {
    // Collect from selectPeopleStore
    if (
      selectPeopleStore &&
      selectPeopleStore.selectUserData['CUSTOM_VIEWERS']
    ) {
      const viewers = selectPeopleStore.selectUserData['CUSTOM_VIEWERS'].map(
        u => u.id
      )
      setCustomViewers(viewers)
    }
    setShowSelectPeople(false)
  }

  const handleSelectTaggedUsers = () => {
    if (selectPeopleStore && selectPeopleStore.selectUserData['TAGGED_USERS']) {
      const users = selectPeopleStore.selectUserData['TAGGED_USERS'].map(
        u => u.username || u.code || u.id
      )
      setTaggedUsers(users)
    }
    setShowSelectPeopleTagged(false)
  }

  const handleRemoveTaggedUser = useCallback(
    userId => {
      setTaggedUsers(prev => prev.filter(id => id !== userId))
      if (
        selectPeopleStore &&
        selectPeopleStore.selectUserData['TAGGED_USERS']
      ) {
        selectPeopleStore.selectUserData[
          'TAGGED_USERS'
        ] = selectPeopleStore.selectUserData['TAGGED_USERS'].filter(
          u => (u.username || u.code || u.id) !== userId
        )
      }
    },
    [selectPeopleStore]
  )

  const getTaggedUserDisplay = useCallback(
    userId => {
      const allUsers = [
        ...(userStore?.mentionUserList || []),
        ...(userStore?.userList || []),
      ]
      const found = allUsers.find(
        u =>
          (u.username &&
            u.username.toLowerCase() === String(userId).toLowerCase()) ||
          (u.code &&
            String(u.code).toLowerCase() === String(userId).toLowerCase()) ||
          (u.id && String(u.id).toLowerCase() === String(userId).toLowerCase())
      )
      if (found) {
        return utils.getNameInCapitalize(
          found.display ||
            found.name_uppercase ||
            found.full_name ||
            found.fullName ||
            found.username
        )
      }
      return userId
    },
    [userStore]
  )

  return (
    <ComposerContainer className={isFocused ? 'focused' : ''}>
      {showSelectPeople && (
        <SelectPeoplePopup
          isVisibleSelectPeoplePopup={showSelectPeople}
          handleSubmitSelectUser={handleSelectCustomViewers}
          handleCancelSelectUser={() => setShowSelectPeople(false)}
          title="Chọn người được xem bài viết"
          onlySelectOne={false}
          disableSelectDepartment={true}
        />
      )}
      {/* Mention dropdown moved inside textarea wrapper below for correct positioning */}
      {showSelectPeopleTagged && (
        <SelectPeoplePopup
          isVisibleSelectPeoplePopup={showSelectPeopleTagged}
          handleSubmitSelectUser={handleSelectTaggedUsers}
          handleCancelSelectUser={() => setShowSelectPeopleTagged(false)}
          title="Gắn thẻ bạn bè"
          onlySelectOne={false}
          disableSelectDepartment={true}
        />
      )}
      {/* Feeling Select Modal */}
      <FeelingSelectModal
        visible={showFeelingModal}
        onClose={() => setShowFeelingModal(false)}
        onSelectFeeling={setSelectedFeeling}
        selectedFeeling={selectedFeeling}
      />

      {/* 1. Header Profile: Avatar + Name (with Feeling ✕) + Visibility Pill */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 14,
        }}>
        <UserAvatar style={{ width: 44, height: 44, flexShrink: 0 }}>
          {avatarSrc ? <img src={avatarSrc} alt="" /> : userName.charAt(0)}
        </UserAvatar>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Row 1: Author Name - Feeling Status ✕ */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 6,
              lineHeight: 1.3,
            }}>
            <strong style={{ fontSize: 15, color: '#050505', fontWeight: 600 }}>
              {utils.getNameInCapitalize(userName)}
            </strong>
            {selectedFeeling && (
              <span
                style={{
                  color: '#65676b',
                  fontSize: 14,
                  fontWeight: 'normal',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}>
                <span>-</span>
                <span style={{ fontSize: 15 }}>{selectedFeeling.emoji}</span>
                <span>
                  cảm thấy{' '}
                  {selectedFeeling.label?.toLowerCase() ||
                    selectedFeeling.text}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedFeeling(null)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#94a3b8',
                    fontSize: 13,
                    padding: '0 2px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    lineHeight: 1,
                  }}
                  title="Xóa cảm xúc">
                  ✕
                </button>
              </span>
            )}
          </div>

          {/* Row 2: Visibility Dropdown Pill directly below Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Select
              value={visibility}
              onSelect={val => {
                setVisibility(val)
                if (val === 'CUSTOM') {
                  if (selectPeopleStore) {
                    selectPeopleStore.assigneePopupType = 'CUSTOM_VIEWERS'
                    if (!selectPeopleStore.selectUserData['CUSTOM_VIEWERS']) {
                      selectPeopleStore.selectUserData['CUSTOM_VIEWERS'] = []
                    }
                  }
                  setShowSelectPeople(true)
                }
              }}
              style={{ width: 130 }}
              size="small"
              bordered={false}
              dropdownStyle={{
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
              className="nf-visibility-select">
              <Select.Option value="PUBLIC">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <GlobalOutlined style={{ color: '#1877f2' }} /> Mọi người
                </span>
              </Select.Option>
              <Select.Option value="CUSTOM">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <UsergroupAddOutlined style={{ color: '#1877f2' }} /> Tùy chỉnh
                </span>
              </Select.Option>
            </Select>

            {visibility === 'CUSTOM' && (
              <span
                onClick={() => {
                  if (selectPeopleStore) {
                    selectPeopleStore.assigneePopupType = 'CUSTOM_VIEWERS'
                  }
                  setShowSelectPeople(true)
                }}
                style={{
                  fontSize: 12,
                  color: '#1877f2',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}>
                {customViewers.length > 0
                  ? `(Đã chọn ${customViewers.length} người)`
                  : '(Chọn người xem)'}
              </span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .nf-visibility-select {
          background: #f0f2f5 !important;
          border-radius: 16px !important;
          padding: 2px 4px !important;
          font-weight: 500 !important;
          color: #050505 !important;
          transition: all 0.2s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          cursor: pointer !important;
        }
        .nf-visibility-select:hover {
          background: #e4e6eb !important;
        }
        .nf-visibility-select .ant-select-selector {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          padding: 0 4px !important;
          height: auto !important;
        }
        .nf-visibility-select .ant-select-selection-item {
          display: flex !important;
          align-items: center !important;
          gap: 6px !important;
          font-size: 13px !important;
          color: #050505 !important;
          padding: 0 !important;
        }
      `}</style>

      {/* 2. Full Width CKEditor Box */}
      <NewsFeedEditor
        value={text}
        onChange={val => setText(val)}
        placeholder={`${utils.getNameInCapitalize(
          userName
        )} ơi, bạn đang nghĩ gì thế?`}
        minHeight="140px"
        maxHeight="340px"
        disabled={isSubmitting}
        userStore={userStore}
      />

      {/* 3. Quick Actions row right below editor: Gắn thẻ bạn bè + Cảm xúc status */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginTop: 12,
          marginBottom: taggedUsers.length > 0 ? 8 : 0,
          flexWrap: 'wrap',
        }}>
        <span
          onClick={() => {
            if (selectPeopleStore) {
              selectPeopleStore.assigneePopupType = 'TAGGED_USERS'
              if (!selectPeopleStore.selectUserData['TAGGED_USERS']) {
                selectPeopleStore.selectUserData['TAGGED_USERS'] = []
              }
            }
            setShowSelectPeopleTagged(true)
          }}
          style={{
            color: '#1877f2',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}>
          <UsergroupAddOutlined style={{ fontSize: 16 }} />
          Gắn thẻ bạn bè
        </span>

        {selectedFeeling && (
          <span
            onClick={() => setShowFeelingModal(true)}
            style={{
              color: '#d97706',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}>
            <SmileOutlined style={{ fontSize: 16, color: '#f59e0b' }} />
            <span>{selectedFeeling.emoji}</span>
            <span>{selectedFeeling.label || selectedFeeling.text}</span>
          </span>
        )}
      </div>

      {/* 4. Tagged Users Green Box (matching Image 1) */}
      {taggedUsers.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 8,
            marginBottom: 4,
            padding: '6px 12px',
            background: '#f0fdf4',
            borderRadius: 8,
            border: '1px solid #bbf7d0',
          }}>
          <strong style={{ color: '#16a34a', fontSize: 13 }}>Cùng với:</strong>
          {taggedUsers.map(uId => (
            <span
              key={uId}
              style={{
                background: '#ffffff',
                border: '1px solid #86efac',
                borderRadius: 16,
                padding: '2px 10px',
                color: '#15803d',
                fontSize: 13,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 500,
              }}>
              <span>{getTaggedUserDisplay(uId)}</span>
              <span
                onClick={() => handleRemoveTaggedUser(uId)}
                style={{
                  cursor: 'pointer',
                  color: '#16a34a',
                  fontSize: 12,
                  fontWeight: 'bold',
                  lineHeight: 1,
                }}
                title="Gỡ gắn thẻ">
                ✕
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Custom Viewers Tag List if visibility is CUSTOM */}
      {visibility === 'CUSTOM' &&
        selectPeopleStore.selectUserData['CUSTOM_VIEWERS'] &&
        selectPeopleStore.selectUserData['CUSTOM_VIEWERS'].length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 8,
              padding: '4px 8px',
              background: '#f8fafc',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}>
            <strong style={{ color: '#475569', fontSize: 13, alignSelf: 'center' }}>
              Người xem:
            </strong>
            {selectPeopleStore.selectUserData['CUSTOM_VIEWERS'].map(user => (
              <Tag
                key={user.id}
                closable
                onClose={e => {
                  e.preventDefault()
                  handleRemoveViewer(user.id)
                }}
                color="blue"
                style={{
                  borderRadius: 16,
                  fontSize: 13,
                  padding: '2px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  margin: 0,
                  fontWeight: 500,
                }}>
                {utils.getNameInCapitalize(
                  user.full_name ||
                    user.name_uppercase ||
                    user.nameUppercase ||
                    user.displayName ||
                    user.username ||
                    'Người dùng'
                )}
              </Tag>
            ))}
          </div>
        )}


      {/* Character count */}
      {text.length > 0 && (
        <div
          style={{
            textAlign: 'right',
            fontSize: 12,
            color: '#888',
            marginTop: 4,
          }}>
          <span
            className={text.length > MAX_TEXT_LENGTH * 0.9 ? 'warning' : ''}>
            {text.length}/{MAX_TEXT_LENGTH}
          </span>
        </div>
      )}

      {/* Media Preview: carousel + fullscreen + caption từng ảnh */}
      {mediaFiles.length > 0 && (
        <ComposerMediaEditor
          mediaFiles={mediaFiles}
          disabled={isSubmitting}
          onClearAll={clearAllMedia}
          onRemoveItem={media => removeMedia(media.id)}
          onChangeCaption={(media, caption) => {
            setMediaFiles(prev =>
              prev.map(item =>
                item.id === media.id ? { ...item, caption } : item
              )
            )
          }}
        />
      )}

      {/* Upload Progress */}
      {isSubmitting && uploadProgress > 0 && (
        <div style={{ marginTop: 12 }}>
          <Progress
            percent={uploadProgress}
            strokeColor={{
              '0%': '#2C65AC',
              '100%': '#10B981',
            }}
            size="small"
          />
        </div>
      )}

      {/* Action Bar */}
      <div style={{ position: 'relative' }}>
        <ComposerActionsRow>
          <ComposerActionButtons>
            <ComposerActionButton
              className="media"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}>
              <PictureOutlined />
              <span>Ảnh/Video/File</span>
            </ComposerActionButton>
            <ComposerActionButton
              className="tag"
              onClick={() => {
                if (selectPeopleStore) {
                  selectPeopleStore.assigneePopupType = 'TAGGED_USERS'
                  if (!selectPeopleStore.selectUserData['TAGGED_USERS']) {
                    selectPeopleStore.selectUserData['TAGGED_USERS'] = []
                  }
                }
                setShowSelectPeopleTagged(true)
              }}
              disabled={isSubmitting}>
              <UsergroupAddOutlined style={{ color: '#16a34a' }} />
              <span>Gắn thẻ</span>
            </ComposerActionButton>
            <ComposerActionButton
              className="feeling"
              onClick={() => setShowFeelingModal(true)}
              style={{
                color: selectedFeeling ? '#f59e0b' : undefined,
                borderColor: selectedFeeling ? '#fde68a' : undefined,
                background: selectedFeeling ? '#fffbeb' : undefined,
              }}
              disabled={isSubmitting}>
              <span
                style={{
                  fontSize: 18,
                  marginRight: 4,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                {selectedFeeling ? selectedFeeling.emoji : '😊'}
              </span>
              <span>
                {selectedFeeling ? selectedFeeling.label : 'Cảm xúc'}
              </span>
            </ComposerActionButton>
          </ComposerActionButtons>

          <ComposerSubmitButton
            className={hasContent ? 'active' : ''}
            onClick={handleSubmit}
            disabled={!hasContent || isSubmitting}>
            {isSubmitting ? (
              <>
                <LoadingOutlined spin /> Đang đăng...
              </>
            ) : (
              'Đăng'
            )}
          </ComposerSubmitButton>
        </ComposerActionsRow>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime,application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </ComposerContainer>
  )
}

export default inject(
  'newsFeedStore',
  'selectPeopleStore',
  'authenticationStore',
  'userStore'
)(observer(PostComposer))
